import { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useQuery } from "@tanstack/react-query";

import { getUsersCompare, type UsersCompareCandidate } from "@/api/users";
import { PageHeader } from "@/components/PageHeader";
import { SearchableSelect } from "@/components/SearchableSelect";

function buildChartOption(
  payload: {
    labels: string[];
    baseline: { label: string; series: number[] };
    candidates: UsersCompareCandidate[];
  },
  selectedIds: (number | null)[],
) {
  const body = document.body ? getComputedStyle(document.body) : null;
  const textColor = body?.getPropertyValue("--text").trim() || "#E8ECF1";
  const textMuted = body?.getPropertyValue("--text-muted").trim() || "#A0AAB8";
  const borderColor = body?.getPropertyValue("--border").trim() || "rgba(42,52,68,0.5)";
  const isLight = document.documentElement.dataset.uiTheme === "light";
  const tooltipBg = isLight ? "rgba(255,253,249,0.96)" : "rgba(10,14,20,0.96)";

  const activeCandidates = selectedIds
    .map((id) => payload.candidates.find((c) => c.id === id) ?? null)
    .filter((c): c is UsersCompareCandidate => c !== null);

  const legendData = [payload.baseline.label, ...activeCandidates.map((c) => c.username)];

  return {
    animationDuration: 700,
    grid: { top: 28, right: 20, bottom: 30, left: 38 },
    legend: {
      data: legendData,
      top: 0,
      right: 0,
      itemWidth: 10,
      itemHeight: 10,
      icon: "circle",
      textStyle: { color: textMuted, fontSize: 11 },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: tooltipBg,
      borderColor,
      borderWidth: 1,
      textStyle: { color: textColor },
    },
    xAxis: {
      type: "category",
      data: payload.labels,
      axisLine: { lineStyle: { color: borderColor } },
      axisTick: { show: false },
      axisLabel: { color: textMuted },
    },
    yAxis: {
      type: "value",
      min: 20,
      max: 100,
      splitNumber: 6,
      axisLabel: { color: textMuted },
      splitLine: { lineStyle: { color: borderColor, type: "dashed" } },
    },
    series: [
      {
        name: payload.baseline.label,
        type: "line",
        data: payload.baseline.series,
        smooth: 0.25,
        symbol: "none",
        lineStyle: { width: 2, color: "#A0AAB8", type: "dashed" },
      },
      ...activeCandidates.map((candidate) => ({
        name: candidate.username,
        type: "line",
        data: candidate.series,
        smooth: 0.22,
        symbol: "circle",
        symbolSize: 7,
        lineStyle: { width: 3, color: candidate.color },
        itemStyle: { color: candidate.color, borderColor: "#0A0E14", borderWidth: 2 },
      })),
    ],
  };
}

export function UsersComparePage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["users", "compare"],
    queryFn: getUsersCompare,
  });

  const defaultIds = useMemo(
    () => data?.payload.default_selected_ids ?? [],
    [data],
  );
  const maxSlots = data?.payload.max_selection ?? 4;

  const [slots, setSlots] = useState<(number | null)[]>([]);

  const activeSlots = useMemo(() => {
    if (slots.length === maxSlots) return slots;
    const next = new Array<number | null>(maxSlots).fill(null);
    defaultIds.slice(0, maxSlots).forEach((id, idx) => {
      next[idx] = id;
    });
    return next;
  }, [slots, defaultIds, maxSlots]);

  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load pilot compare</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  const { payload, summary } = data;

  function handleSlotChange(index: number, value: string) {
    const id = value ? Number(value) : null;
    const next = [...activeSlots];
    next[index] = id;
    setSlots(next);
  }

  if (!payload.candidates.length) {
    return (
      <>
        <PageHeader title="Pilot Compare" />
        <section className="panel">
          <p className="empty-state">
            No pilots with flight data are available yet for the compare mockup.
          </p>
        </section>
      </>
    );
  }

  const selectedCandidates = activeSlots
    .map((id) => payload.candidates.find((c) => c.id === id) ?? null)
    .filter((c): c is UsersCompareCandidate => c !== null);

  const leading = selectedCandidates.slice().sort((a, b) => b.current_score - a.current_score)[0];
  const baselineFinal = payload.baseline.series[payload.baseline.series.length - 1] ?? null;
  const leadDelta =
    leading && baselineFinal !== null ? leading.current_score - baselineFinal : null;

  return (
    <>
      <PageHeader
        title="Pilot Compare"
        lede="Select multiple real pilots, compare them against each other, and read the mocked development-line interpretation."
        actions={
          <div className="stats-row">
            <article className="stat-card">
              <span>Visible pilots</span>
              <strong>{summary.pilots}</strong>
            </article>
            <article className="stat-card">
              <span>Active pilots</span>
              <strong>{summary.active_pilots}</strong>
            </article>
            <article className="stat-card">
              <span>Visible flights</span>
              <strong>{summary.flights}</strong>
            </article>
          </div>
        }
      />

      <section className="panel mock-panel mock-watermarked directory-compare-panel">
        <div className="panel-head">
          <div>
            <div className="mock-badges">
              <span className="mock-badge mock-badge-hot">MOCKUP</span>
              <span className="mock-badge">REAL PILOT SELECTOR</span>
            </div>
            <h2>Compare</h2>
            <p>
              Select multiple real pilots and compare a mocked readiness curve against each other
              and the dotted average pilot development line.
            </p>
          </div>
        </div>

        <div className="directory-compare-grid">
          <aside className="directory-compare-sidebar">
            <div className="directory-compare-toolbar">
              <strong>Choose pilots</strong>
              <span>Up to {maxSlots} at once</span>
            </div>
            <div className="directory-compare-selection-note">
              {payload.candidates.length} pilots available
            </div>
            <div className="directory-compare-slot-grid">
              {activeSlots.map((selectedId, index) => {
                const candidate = payload.candidates.find((c) => c.id === selectedId);
                return (
                  <label className="directory-compare-select-wrap" key={index}>
                    <span className="directory-slot-card">
                      <span className="directory-slot-head">
                        <span className="directory-slot-label">Pilot {index + 1}</span>
                        <span className="directory-slot-badge">
                          {candidate ? "Selected" : "Open"}
                        </span>
                      </span>
                      <span className="directory-slot-preview">
                        <span
                          className="directory-slot-line"
                          style={{
                            background: candidate?.color ?? "var(--border)",
                          }}
                          aria-hidden="true"
                        />
                        <span className="directory-slot-preview-copy">
                          <strong>{candidate ? candidate.username : "None selected"}</strong>
                          <small>
                            {candidate
                              ? `Current score ${Math.round(candidate.current_score)}/100`
                              : "Choose a pilot for this compare slot."}
                          </small>
                        </span>
                      </span>
                      <span className="directory-slot-select-shell">
                        <SearchableSelect
                          className="directory-compare-select"
                          value={selectedId != null ? String(selectedId) : ""}
                          onChange={(v) => handleSlotChange(index, v)}
                          minWidth={220}
                          searchPlaceholder="Search pilot…"
                          ariaLabel="Select pilot for compare slot"
                          searchable={payload.candidates.length > 6}
                          options={[
                            { value: "", label: "None" },
                            ...payload.candidates.map((c) => ({
                              value: String(c.id),
                              label: `${c.username} · ${Math.round(c.current_score)}/100`,
                            })),
                          ]}
                        />
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="directory-compare-select-help">
              Each slot uses the same pilot list. Leave a slot on "None" if you want fewer than
              four lines.
            </p>

            <div className="directory-compare-meta">
              <article className="mock-summary-card">
                <span>Development line</span>
                <strong>
                  {baselineFinal !== null ? `${Math.round(baselineFinal)} / 100` : "-"}
                </strong>
                <small>
                  Mocked reference showing how an average pilot would usually develop across the
                  same readiness timeline.
                </small>
              </article>
              <article className="mock-summary-card">
                <span>Current lead</span>
                <strong>{leading ? leading.username : "-"}</strong>
                <small>
                  {leading
                    ? `${Math.round(leading.current_score)} / 100 on the mocked trend.`
                    : "Select pilots to see who is leading the mocked trend."}
                </small>
              </article>
              <article className="mock-summary-card">
                <span>Spread to development</span>
                <strong>
                  {leadDelta !== null
                    ? `${leadDelta >= 0 ? "+" : ""}${leadDelta.toFixed(1)}`
                    : "-"}
                </strong>
                <small>
                  The chart uses real pilot accounts but the score itself is a prototype
                  visualization.
                </small>
              </article>
            </div>
          </aside>

          <div className="directory-compare-main">
            <div className="mock-chart-stage directory-compare-chart">
              <ReactECharts
                option={buildChartOption(payload, activeSlots)}
                notMerge
                style={{ height: "100%", width: "100%" }}
              />
            </div>
            <div className="directory-compare-copy">
              <h3>Mocked graph readout</h3>
              <p>
                This prototype converts real pilot aggregates into a mocked readiness index.
                Smoothness carries the most weight, then speed discipline, route workload, and
                mission repetition. The dotted line represents an average pilot development path,
                not a live cohort mean.
              </p>
              <div className="directory-compare-insights">
                {selectedCandidates.slice(0, 3).map((candidate) => (
                  <article className="mock-summary-card" key={candidate.id}>
                    <span className="directory-insight-label">
                      <span
                        className="directory-insight-line"
                        style={{ ["--directory-line-color" as never]: candidate.color } as React.CSSProperties}
                      />
                      <span>{candidate.username}</span>
                    </span>
                    <strong>{Math.round(candidate.current_score)} / 100</strong>
                    <small>
                      {candidate.focus_title}. {candidate.mock_profile}
                    </small>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
